import api from 'helpers/api';
import API_ENDPOINT from 'constants/apiEndpoint';
import { uploadRejection } from 'constants/mediaUpload';
import { TApiResult } from 'types/common';

import {
  TMediaItem,
  TMediaListParams,
  TMediaPage,
  TMediaType,
  TRegisterMediaBody,
  TUploadTicket,
  TUploadUrlBody,
} from './media.types';

export const getMediaList = async (params: TMediaListParams): Promise<TMediaPage> => {
  const raw = await api.get<TMediaPage | TApiResult<TMediaPage>>(API_ENDPOINT.media.list, {
    params,
  });
  const page = 'result' in raw ? raw.result : raw;
  return {
    ...page,
    isLastPage: page.isLastPage ?? page.content.length < params.size,
    totalPages: page.totalPages ?? 0,
  };
};

const URL_MAP_PAGE_SIZE = 100;
const URL_MAP_MAX_PAGES = 10;

export const getMediaUrlMap = async (): Promise<Record<string, string>> => {
  const first = await getMediaList({ page: 0, size: URL_MAP_PAGE_SIZE });
  const totalPages = Math.min(first.totalPages ?? 1, URL_MAP_MAX_PAGES);
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, totalPages - 1) }, (_unused, index) =>
      getMediaList({ page: index + 1, size: URL_MAP_PAGE_SIZE })
    )
  );

  return [first, ...rest].reduce<Record<string, string>>((map, page) => {
    page.content.forEach((item) => {
      map[item.id] = item.mediaUrl;
    });
    return map;
  }, {});
};

const requestUploadUrl = (body: TUploadUrlBody): Promise<TUploadTicket> =>
  api.post<TApiResult<TUploadTicket>>(API_ENDPOINT.media.uploadUrl, body).then((r) => r.result);

const registerMedia = (body: TRegisterMediaBody): Promise<TMediaItem> =>
  api.post<TApiResult<TMediaItem>>(API_ENDPOINT.media.create, body).then((r) => r.result);

export const deleteMedia = (id: string): Promise<void> =>
  api.delete<void>(API_ENDPOINT.media.item(id));

/**
 * Rewrites an S3 URL onto the same-origin Vite proxy (see `VITE_S3_PROXY`), used by the upload PUT.
 *
 * The `wamo-static-assets` bucket now returns CORS headers for the spotlight origins (GET/HEAD), so
 * plain `<img>`/`<video>` display and `crossOrigin` reads of the public `mediaUrl` work directly and
 * do not need this proxy. It remains useful for the local dev PUT, where signing against the exact
 * S3 host is simpler through a same-origin proxy. Reads are never presigned GETs: the `mediaUrl`
 * is an unsigned public URL, so a signed read (esp. one that signs content-type) that 403s with
 * SignatureDoesNotMatch points at a stale campaign-api deploy, not a CORS problem.
 */
export const toS3ProxyUrl = (fileUrl: string): string => {
  if (import.meta.env.VITE_S3_PROXY !== 'true') {
    return fileUrl;
  }
  try {
    const url = new URL(fileUrl);
    return `/__s3${url.pathname}${url.search}`;
  } catch {
    return fileUrl;
  }
};

const putToS3 = async (uploadUrl: string, file: Blob, contentType: string): Promise<void> => {
  const response = await fetch(toS3ProxyUrl(uploadUrl), {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Media upload to storage failed (${response.status}).`);
  }
};

export const uploadMedia = async (
  file: Blob,
  mediaType: TMediaType,
  filename = 'poster.jpg'
): Promise<TMediaItem> => {
  const contentType = file.type || (mediaType === 'VIDEO' ? 'video/mp4' : 'image/jpeg');
  const rejection = uploadRejection(file, contentType);
  if (rejection) {
    throw new Error(rejection);
  }
  const ticket = await requestUploadUrl({
    fileName: filename,
    contentType,
    sizeBytes: file.size,
  });
  await putToS3(ticket.uploadUrl, file, contentType);
  return registerMedia({ title: filename, key: ticket.key });
};
